import { CheckCircle2, Rocket, ArrowRight, LayoutGrid } from "lucide-react";

const PERKS_FREE = ["Zero payment", "Instant publish", "Owned for lifetime"];
const PERKS_CUSTOM = [
  "Click and drag any size you want on the canvas grid",
  "Zero size restrictions — from single blocks to massive banners",
  "Upload a single, high-definition unified image across your selection",
  "Real-time hover tooltip metrics and click-through analytics tracker",
  "Permanent placement with 100% lifetime ownership",
];

export default function Pricing({ promo }) {
  const isFree = promo?.is_free_phase;

  const scrollToGrid = () => {
    const el = document.querySelector("#explore");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.location.href = "/#explore";
  };

  return (
    <section id="pricing" data-testid="pricing-section" className="max-w-6xl mx-auto px-4 py-24">
      <div className="text-center max-w-2xl mx-auto">
        <div className="pill mx-auto"><span>{isFree ? "Launch phase" : "USD · one-time"}</span></div>
        <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-none text-white">
          {isFree
            ? <>The first 50 blocks are <span className="text-gradient">FREE.</span></>
            : <>Neon-simple pricing. <span className="text-gradient">Yours forever.</span></>}
        </h2>
        <p className="mt-4 text-[var(--fg-muted)]">
          {isFree
            ? `${promo.remaining} of ${promo.total} free pixel-blocks remaining — no card required, no strings attached.`
            : "Secure your piece of internet history for just $0.50 per pixel. Standard 10×10 block units start at $50. One-time payment for lifetime real estate."}
        </p>
      </div>

      <div className="mt-14 grid lg:grid-cols-5 gap-4 items-stretch">
        {/* Launch promo card */}
        <div
          data-testid="tier-free"
          className={`lg:col-span-2 card-elev p-6 relative flex flex-col ${isFree ? "ring-2 ring-violet-500 shadow-neon" : ""}`}
          style={isFree ? { boxShadow: "var(--shadow-neon-violet), var(--shadow-lg)" } : {}}
        >
          {isFree && (
            <div className="absolute -top-3 left-6 pill" style={{ background: "hsl(var(--primary))", color: "white", borderColor: "hsl(var(--primary))" }}>
              Live now
            </div>
          )}
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-emerald-500 flex items-center justify-center">
            <Rocket className="w-5 h-5 text-white" />
          </div>
          <div className="mt-4 text-xs uppercase tracking-widest text-[var(--fg-muted)]">Launch promo</div>
          <div className="mt-1 font-display text-5xl font-medium">{isFree ? "$0" : "—"}</div>
          <div className="text-sm text-[var(--fg-muted)]">
            {isFree ? `First 50 pixel-blocks · ${promo.remaining} left` : "Sold out"}
          </div>
          <ul className="mt-5 space-y-2 text-sm">
            {PERKS_FREE.map((p) => (
              <li key={p} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {p}
              </li>
            ))}
          </ul>
        </div>

        {/* Custom Empire Builder — the ultra-premium featured card */}
        <div
          data-testid="tier-custom-empire"
          className="lg:col-span-3 relative rounded-2xl p-[1px] overflow-hidden group"
          style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.9), rgba(6,182,212,0.9) 50%, rgba(236,72,153,0.9))" }}
        >
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(600px circle at 80% 20%, rgba(139,92,246,0.35), transparent 60%), radial-gradient(500px circle at 20% 80%, rgba(6,182,212,0.30), transparent 60%)" }} />
          <div className="relative h-full rounded-[15px] bg-[rgba(10,10,16,0.94)] backdrop-blur-xl p-6 sm:p-8 flex flex-col">
            <div className="absolute -top-3 right-6 pill" style={{ background: "linear-gradient(90deg, #8b5cf6, #06b6d4)", color: "white", borderColor: "transparent" }}>
              Most flexible
            </div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-violet-500 via-cyan-400 to-fuchsia-500 flex items-center justify-center">
                <LayoutGrid className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Custom Empire Builder</div>
                <div className="text-lg font-display leading-tight">Build your own real estate</div>
              </div>
            </div>

            <div className="mt-6 flex items-baseline gap-3 flex-wrap">
              <span className="font-display text-6xl font-medium">$0.50</span>
              <span className="text-lg text-[var(--fg-muted)]">/ pixel</span>
              <span
                data-testid="custom-empire-subtitle"
                className="text-sm px-3 py-1 rounded-full border border-white/10 bg-white/[0.04] font-mono"
              >
                $0.50 per pixel ($50 per 10×10 block unit)
              </span>
            </div>

            <ul className="mt-6 space-y-3 text-sm">
              {PERKS_CUSTOM.map((p) => (
                <li key={p} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-cyan-300 mt-0.5 flex-shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="grow" />

            <button
              type="button"
              onClick={scrollToGrid}
              data-testid="custom-empire-cta"
              className="mt-8 self-start inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium text-white transition hover:scale-[1.02] active:scale-100"
              style={{ background: "linear-gradient(90deg, #8b5cf6, #06b6d4)", boxShadow: "0 12px 30px rgba(139,92,246,0.4)" }}
            >
              Go to Grid & Drag to Build <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <p className="mt-8 text-center text-sm text-[var(--fg-muted)]">
        Every purchase is a one-time payment. Own your pixels for lifetime — no subscriptions, no renewals.
      </p>
    </section>
  );
}
